/**
 * useKnowledge — the React binding for the Knowledge feature.
 *
 * Strict layering: pages call THIS hook; the hook constructs the
 * {@link KnowledgeGateway} and exposes it plus eagerly-loaded package/collection
 * lists. Pages never import a gateway class or the api facade directly.
 *
 * {@link buildGateway} always wires the real, `api.knowledge`-backed
 * {@link RealKnowledgeGateway} — the Spec Studio (Code Knowledge) backend is
 * live.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import type { EngineeringVectorApi } from '../../api';
import type { KnowledgeGateway } from './knowledgeGateway';
import { RealKnowledgeGateway } from './realKnowledgeGateway';
import type { KnowledgeCollection, KnowledgePackage } from './types';

/** Wires the Knowledge feature to the real Spec Studio backend. */
function buildGateway(api: EngineeringVectorApi): KnowledgeGateway {
  return new RealKnowledgeGateway(api.knowledge);
}

export interface UseKnowledgeResult {
  /** The active gateway; pages call its methods for on-demand data. */
  gateway: KnowledgeGateway;
  /** Data-source mode — always 'REAL'; surfaced for the status indicator. */
  mode: KnowledgeGateway['mode'];
  /** All packages (eagerly loaded on mount). */
  packages: KnowledgePackage[];
  /** All collections (eagerly loaded on mount). */
  collections: KnowledgeCollection[];
  loading: boolean;
  error: string | null;
  /** Re-fetches the package + collection lists. */
  refresh: () => Promise<void>;
}

export function useKnowledge(): UseKnowledgeResult {
  const api = useApi();
  const gateway = useMemo(() => buildGateway(api), [api]);

  const [packages, setPackages] = useState<KnowledgePackage[]>([]);
  const [collections, setCollections] = useState<KnowledgeCollection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [pkgs, cols] = await Promise.all([
        gateway.listPackages(),
        gateway.listCollections(),
      ]);
      pkgs.sort((a, b) => a.name.localeCompare(b.name));
      setPackages(pkgs);
      setCollections(cols);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load knowledge.');
    } finally {
      setLoading(false);
    }
  }, [gateway]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    gateway,
    mode: gateway.mode,
    packages,
    collections,
    loading,
    error,
    refresh,
  };
}
